import React, { useEffect, useState } from 'react';
import {
  Sun,
  Moon,
  Cloud,
  CloudSun,
  CloudRain,
  CloudSnow,
  CloudLightning,
  CloudFog,
  Wind,
  Droplets,
} from 'lucide-react';

// NWS gridpoint for Telluride (37.9375, -107.8123). The grid mapping for a
// fixed lat/lon does not change, so this is safe to hardcode.
const FORECAST_URL = 'https://api.weather.gov/gridpoints/GJT/116,49/forecast';
const CACHE_KEY = 'nws-forecast-v1';
const CACHE_TTL_MS = 30 * 60 * 1000;

const iconFor = (shortForecast = '', isDaytime = true) => {
  const f = shortForecast.toLowerCase();
  if (/thunder/.test(f)) return CloudLightning;
  if (/snow|flurr|blizzard|sleet|wintry/.test(f)) return CloudSnow;
  if (/rain|shower|drizzle/.test(f)) return CloudRain;
  if (/fog|haze|smoke/.test(f)) return CloudFog;
  if (/windy|breezy|blustery/.test(f)) return Wind;
  if (/partly|mostly sunny|mostly clear/.test(f)) return isDaytime ? CloudSun : Cloud;
  if (/cloudy|overcast/.test(f)) return Cloud;
  if (/sunny|clear/.test(f)) return isDaytime ? Sun : Moon;
  return isDaytime ? CloudSun : Cloud;
};

// Tint the current-conditions card by what's happening outside.
const tintFor = (shortForecast = '', isDaytime = true) => {
  const f = shortForecast.toLowerCase();
  if (/thunder|rain|shower|drizzle/.test(f)) return 'from-slate-600 to-blue-900';
  if (/snow|flurr|blizzard|sleet|wintry/.test(f)) return 'from-sky-400 to-indigo-600';
  if (/fog|haze|smoke|cloudy|overcast/.test(f)) return 'from-slate-500 to-slate-700';
  if (!isDaytime) return 'from-indigo-800 to-slate-900';
  if (/sunny|clear/.test(f)) return 'from-amber-400 to-orange-600';
  return 'from-sky-500 to-blue-700';
};

const shortDay = (name) => {
  // NWS period names: "Tonight", "Thursday", "Thursday Night", "This Afternoon"
  const m = name.match(/^(Sun|Mon|Tue|Wed|Thu|Fri|Sat)/);
  return m ? m[1] : name;
};

const readCache = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { at, periods } = JSON.parse(raw);
    if (Date.now() - at > CACHE_TTL_MS) return null;
    return periods;
  } catch {
    return null;
  }
};

const writeCache = (periods) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), periods }));
  } catch {
    // storage unavailable; ignore
  }
};

const Weather = () => {
  const [periods, setPeriods] = useState(() => readCache());
  const [status, setStatus] = useState(periods ? 'ready' : 'loading');

  useEffect(() => {
    if (periods) return;
    let cancelled = false;
    fetch(FORECAST_URL, { headers: { Accept: 'application/geo+json' } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
      .then((json) => {
        if (cancelled) return;
        const p = json?.properties?.periods;
        if (!Array.isArray(p) || p.length === 0) throw new Error('empty');
        writeCache(p);
        setPeriods(p);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [periods]);

  if (status === 'error') {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Live forecast unavailable right now. Use the links below.
      </p>
    );
  }

  if (status === 'loading') {
    return (
      <div className="animate-pulse grid grid-cols-1 md:grid-cols-3 gap-4" aria-busy="true">
        <div className="h-32 rounded-xl bg-gray-200 dark:bg-gray-700 md:col-span-1" />
        <div className="h-32 rounded-xl bg-gray-200 dark:bg-gray-700 md:col-span-2" />
      </div>
    );
  }

  const now = periods[0];
  const NowIcon = iconFor(now.shortForecast, now.isDaytime);
  const pop = now.probabilityOfPrecipitation?.value;
  const upcoming = periods.filter((p) => p.isDaytime && p.number !== now.number).slice(0, 5);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Current period */}
      <div
        className={`rounded-xl bg-gradient-to-br ${tintFor(now.shortForecast, now.isDaytime)} text-white p-6 flex items-center justify-between md:col-span-1 shadow-md`}
      >
        <div>
          <p className="text-sm uppercase tracking-wide text-white/80">{now.name}</p>
          <p className="text-5xl font-bold leading-none mt-1">
            {now.temperature}°<span className="text-2xl align-top">{now.temperatureUnit}</span>
          </p>
          <p className="mt-2 text-white/90">{now.shortForecast}</p>
          <p className="mt-1 text-xs text-white/80 flex items-center gap-3">
            {typeof pop === 'number' && pop > 0 && (
              <span className="inline-flex items-center gap-1">
                <Droplets className="h-3 w-3" /> {pop}%
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <Wind className="h-3 w-3" /> {now.windSpeed} {now.windDirection}
            </span>
          </p>
        </div>
        <NowIcon className="h-16 w-16 text-white/90 flex-shrink-0" strokeWidth={1.5} />
      </div>

      {/* Next days */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 md:col-span-2">
        <div className="grid grid-cols-5 gap-2 text-center">
          {upcoming.map((p) => {
            const Icon = iconFor(p.shortForecast, true);
            const chance = p.probabilityOfPrecipitation?.value;
            return (
              <div key={p.number} className="flex flex-col items-center py-2">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  {shortDay(p.name)}
                </span>
                <Icon className="h-7 w-7 my-2 text-[var(--accent)]" strokeWidth={1.75} />
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  {p.temperature}°
                </span>
                <span className="text-[11px] text-gray-500 dark:text-gray-400 h-4">
                  {typeof chance === 'number' && chance >= 20 ? `${chance}%` : ''}
                </span>
              </div>
            );
          })}
        </div>
        <p className="mt-2 text-[11px] text-right text-gray-500 dark:text-gray-400">
          Forecast by the{' '}
          <a
            href="https://forecast.weather.gov/MapClick.php?lat=37.9375&lon=-107.8123"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-[var(--accent)]"
          >
            National Weather Service
          </a>
        </p>
      </div>
    </div>
  );
};

export default Weather;
